import React, { Fragment } from 'react';
import { useForcedCartCache } from '../../context/CartCacheContext';
import { StepPropsBase } from '../Reserve';
import CartSummary from '../../components/CartSummary';
import { useEventCache } from '../../context/EventCacheContext';
import TwoColumnLayout from '../../components/TwoColumnLayout';
import ExperienceSummary from '../../components/ExperienceSummary';
import Button from '../../components/Button';

type CartViewProps = StepPropsBase;

export default function CartView({ onBack, onNext }: CartViewProps) {
  const { setEventId } = useEventCache();
  const { cart, swapDrafts, deleteCart, deleteDraft } = useForcedCartCache();

  const numOfItems = cart.workingDrafts.length;
  const nextDisabled = cart.workingDrafts.some(rd => !rd.progress.canProceedToCheckout);

  function handleOnDelete(draftId: string) {
    if (cart.workingDrafts.length < 2) {
      onNext?.('grid', deleteCart);
    } else {
      deleteDraft(draftId);
    }
  }

  function handleOnEdit(draftId: string) {
    const session = cart.workingDrafts.find(rd => rd.reservationDraft._id === draftId);
    if (session) {
      swapDrafts(session);
      setEventId(session.reservationDraft.eventSnapshot?.eventTemplate ?? null);
      onNext?.('date');
    }
  }

  return (
    <TwoColumnLayout
      title={`Your Cart — ${numOfItems} item(s)`}
      subtitle="Cart Summary"
      aside={
        <CartSummary
          onBack={() => onBack?.()}
          onNext={() => onNext?.()}
          backLabel="Add More Experiences"
          nextDisabled={nextDisabled}
        />
      }
    >
      {cart.workingDrafts.map((rd, i) => (
        <Fragment key={i}>
          <ExperienceSummary
            noImage
            draftOverride={rd}
            error={!rd.progress.canProceedToCheckout ? 'This booking is incomplete' : undefined}
            titleButtons={
              <div className="-mr-2 -mt-2 flex flex-wrap items-center gap-2">
                <Button
                  onClick={() => handleOnEdit(rd.reservationDraft._id)}
                  aria-label="Edit item"
                  icon={['fass', 'pen']}
                  variant="borderlike"
                >
                  Edit
                </Button>
                <Button
                  onClick={() => handleOnDelete(rd.reservationDraft._id)}
                  aria-label="Delete item"
                  icon={['fass', 'trash']}
                  variant="borderlike"
                  className="text-error-i"
                >
                  Delete
                </Button>
              </div>
            }
          />
        </Fragment>
      ))}
    </TwoColumnLayout>
  );
}
